import {
  ClickAwayListener,
  createStyles,
  Grow,
  Paper,
  Popper,
} from "@material-ui/core";
import {
  withStyles,
  type Theme,
  type WithStyles,
} from "@material-ui/core/styles";
import clsx from "clsx";
import React, {
  ChangeEvent,
  type ComponentProps,
  type ReactNode,
  type RefObject,
} from "react";
import {
  getBasePlacement,
  getPopperOffset,
  getTransformOrigin,
} from "./menuButtonUtils";
import type { MenuButtonPlacement } from "./menuButton.types";

const POPPER_RADIUS = "8px";

export type MenuButtonProps = {
  children?: ReactNode;
  classNames?: {
    arrow?: string;
    dropdownContainer?: string;
    paper?: string;
    popper?: string;
  };
  disableArrow?: boolean;
  id: string;
  isOpen: boolean;
  onClickAway: (event: ChangeEvent<HTMLElement>) => void;
  paperProps?: ComponentProps<typeof Paper>;
  placement?: MenuButtonPlacement;
  popperProps?: Omit<ComponentProps<typeof Popper>, "children" | "open">;
  trigger?: ReactNode;
  triggerRef: RefObject<HTMLElement>;
} & WithStyles<typeof styles>;

const MenuButton = ({
  children,
  classes,
  classNames = {},
  disableArrow = false,
  id,
  isOpen,
  onClickAway,
  paperProps = {},
  placement = "bottom",
  popperProps = {},
  trigger,
  triggerRef,
}: MenuButtonProps) => {
  const popperOffset = getPopperOffset({ disableArrow, placement });

  const popperClass = clsx(classes.popper, classNames.popper);
  const dropdownContainerClass = clsx(
    classes.dropdownContainer,
    classNames.dropdownContainer
  );
  const paperClass = clsx(classes.paper, classNames.paper);

  return (
    <>
      {trigger}
      <Popper
        anchorEl={triggerRef.current}
        className={popperClass}
        data-testid={id}
        disablePortal={true}
        id={id}
        open={isOpen}
        placement={placement}
        transition={true}
        modifiers={{
          offset: { enabled: true, offset: popperOffset },
        }}
        {...popperProps}
      >
        {({ TransitionProps, placement: actualPlacement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: getTransformOrigin({
                placement: actualPlacement,
              }),
            }}
          >
            <ClickAwayListener
              data-testid={`${id}-click-away`}
              onClickAway={onClickAway as (event: ChangeEvent<unknown>) => void}
            >
              <div className={dropdownContainerClass}>
                {!disableArrow && (
                  <PopperArrow
                    className={classNames.arrow}
                    classes={classes}
                    listId={id}
                    placement={actualPlacement}
                  />
                )}
                <Paper className={paperClass} {...paperProps}>
                  {children}
                </Paper>
              </div>
            </ClickAwayListener>
          </Grow>
        )}
      </Popper>
    </>
  );
};

type PopperArrowProps = {
  className?: string;
  listId: string;
  placement: MenuButtonPlacement;
} & WithStyles<typeof styles>;

const PopperArrow = ({
  className,
  classes,
  listId,
  placement,
}: PopperArrowProps) => {
  const styleMap = {
    "top-start": { left: POPPER_RADIUS, transform: "none" },
    "top-end": { right: POPPER_RADIUS, transform: "none" },
    top: { left: "50%", transform: "translateX(-50%)" },

    "bottom-start": { left: POPPER_RADIUS, transform: "none" },
    "bottom-end": { right: POPPER_RADIUS, transform: "none" },
    bottom: { left: "50%", transform: "translateX(-50%)" },

    "left-start": { top: POPPER_RADIUS, transform: "none" },
    "left-end": { bottom: POPPER_RADIUS, transform: "none" },
    left: { top: "50%", transform: "translateY(-50%)" },

    "right-start": { top: POPPER_RADIUS, transform: "none" },
    "right-end": { bottom: POPPER_RADIUS, transform: "none" },
    right: { top: "50%", transform: "translateY(-50%)" },
  };

  const basePlacement = getBasePlacement(placement);

  const arrowClass = clsx(className, {
    [classes.arrowTop]: basePlacement === "top",
    [classes.arrowBottom]: basePlacement === "bottom",
    [classes.arrowLeft]: basePlacement === "left",
    [classes.arrowRight]: basePlacement === "right",
  });

  const arrowStyles = styleMap[placement];

  return (
    <div
      aria-hidden="true"
      data-testid={`${listId}-popper-arrow`}
      className={arrowClass}
      style={arrowStyles}
    />
  );
};

const styles = (theme: Theme) => {
  const defaultArrowStyle = {
    position: "absolute",
    width: 0,
    height: 0,
    zIndex: 1,
  } as const;

  return createStyles({
    popper: {
      borderRadius: POPPER_RADIUS,
      zIndex: 2000,
    },
    dropdownContainer: {
      position: "relative",
    },
    arrowTop: {
      ...defaultArrowStyle,
      bottom: "-10px",
      filter: "drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.15))",
      borderLeft: "8.66px solid transparent", // ~8.66px for equilateral triangle with 10px height
      borderRight: "8.66px solid transparent",
      borderTop: "10px solid white",
    },
    arrowBottom: {
      ...defaultArrowStyle,
      top: "-10px",
      filter: "drop-shadow(0px -2px 4px rgba(0, 0, 0, 0.15))",
      borderLeft: "8.66px solid transparent",
      borderRight: "8.66px solid transparent",
      borderBottom: "10px solid white",
    },
    arrowLeft: {
      ...defaultArrowStyle,
      right: "-10px",
      filter: "drop-shadow(2px 0px 4px rgba(0, 0, 0, 0.1))",
      borderLeft: "10px solid white",
      borderTop: "8.66px solid transparent",
      borderBottom: "8.66px solid transparent",
    },
    arrowRight: {
      ...defaultArrowStyle,
      left: "-10px",
      filter: "drop-shadow(-2px 0px 4px rgba(0, 0, 0, 0.1))",
      borderRight: "10px solid white",
      borderTop: "8.66px solid transparent",
      borderBottom: "8.66px solid transparent",
    },
    paper: {
      backgroundColor: theme.palette.common.white,
      borderRadius: "8px",
      boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.15)",
    },
  });
};

export default withStyles(styles)(MenuButton);
